import React, { useCallback, useEffect, useRef, useState } from "react";
import AppLayout from "@/Layouts/AppLayout";
import { Dialog, Dropdown, EmptyState } from "@/Components";
import { Search, MoreVertical } from "react-feather";
import { usePage } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import { atom, useAtom } from "jotai";
import { debounce } from "lodash";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import {
  LabeledInput,
  LabeledMultiSelectAutocomplete,
  LabeledSelectAutocomplete,
} from "@/Components/Form";
import { toast } from "react-toastify";
import { Teams as TeamsIcon } from "@/Icons";
import LayoutIcon from "@/Components/LayoutIcon";
import { ActionMenu } from "@/Components/ActionMenu";
import { useSetAtom } from "jotai/index";
import { isNewActivityOpenAtom } from "@/Pages/Reporting/Owner/Index";
import { LoadingProvider } from "@/Contexts/LoadingContext";
import NewActivityChooseType from "@/Components/Team/NewActivityChooseType";
import AgencyQuestionRow from "@/Pages/Agency/Questions/AgencyQuestionRow";
import { Tab, Tabs } from "@/Components/Tabs";
import { showToast } from "@/Utilities/Utilities";

const searchAtom = atom("");
const sortAtom = atom("created.asc");
const scopesAtom = atom(null);
const tagsAtom = atom(null);

export default function Index({ tags }) {
  const questions = usePage().props?.questions?.data || [];
  const global_questions = usePage().props?.global_questions?.data || [];
  const organizations = usePage().props?.organizations?.data || [];
  const createdQuestionId =
    usePage().props?.custom_response_data?.created_question_id || false;

  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [targetOrganization, setTargetOrganization] = useState(null);

  let [modal, setModal] = useState({
    open: false,
    title: "",
    content: "",
    class: "",
  });

  const questionsSortedByNewlyCreated = useCallback(() => {
    if (!createdQuestionId) {
      return questions;
    }

    return questions.sort((q) => (createdQuestionId === q.id ? -1 : 1));
  }, [createdQuestionId, questions]);

  function Row({ question }) {
    return (
      <div className="relative">
        <input
          type="checkbox"
          checked={selectedRows.includes(question.id)}
          onChange={() => handleSelectRow(question.id)}
          className="absolute left-4 top-4 z-10"
        />
        <AgencyQuestionRow
          question={question}
          isNew={createdQuestionId === question.id}
          onDelete={(questionId) => {
            setSelectedRows([questionId]);
            setIsDeleting(true);
          }}
          onAttach={(questionId) => {
            setSelectedRows([questionId]);
            openModal(
              "Add to client",
              "You're about to add this question to a client",
              "Adding this question to a client will make a copy of this question and it will be available for use on any team within the client.",
              "bg-blue",
            );
          }}
        />
      </div>
    );
  }

  function openModal(l, t, d, c) {
    setModal({ open: true, label: l, title: t, content: d, class: c });
  }

  function closeModal() {
    setModal({ open: false, label: "", title: "", content: "", class: "" });
  }

  // Add a new function to handle row selection
  function handleSelectRow(id) {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.includes(id)) {
        return prevSelectedRows.filter((rowId) => rowId !== id);
      } else {
        return [...prevSelectedRows, id];
      }
    });
  }

  const handleDelete = () => {
    const id = toast.loading("Please wait...");

    Inertia.delete(route("agency.question.destroy"), {
      data: { questions: selectedRows },
      onSuccess: () => {
        showToast(id, "The question has been removed from your organization!");
      },
      onError: () => {
        showToast(
          id,
          "Something went wrong when removing the question",
          "error",
        );
      },
      onFinish: () => {
        setIsDeleting(false);
      },
    });
  };

  function handleAddToOrganization() {
    const id = toast.loading("Please wait...");

    Inertia.post(
      route("agency.question.attach", {
        organization: targetOrganization,
      }),
      { questions: selectedRows },
      {
        onSuccess: () => {
          closeModal();
          showToast(id, "Question added successfully to client");
        },
      },
    );
  }

  const actionMenuItems = [
    {
      label: "Add to client",
      callback: () =>
        openModal(
          "Add to client",
          "You're about to add the selected question(s) to a client",
          "Adding these question(s) to a client will make a copy of the question and it will be available for use on any team within the client.",
          "bg-blue",
        ),
    },
    {
      label: "Delete activity",
      callback: () => setIsDeleting(true),
    },
  ];

  const actionMenuRef = useRef(null);

  const QuestionsWrapper = ({ items }) => {
    const setNewActivityDialogOpen = useSetAtom(isNewActivityOpenAtom);

    return (
      <>
        {items.length === 0 ? (
          <EmptyState
            title="You have no global activities yet"
            text="Create activity templates that can be attached to any organization"
            buttonText="New activity"
            buttonOnClick={() => setNewActivityDialogOpen(true)}
          />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid gap-2">{items}</div>
            <div className="shadow-small flex h-12 items-center justify-center gap-4 self-start bg-white py-2 pl-4 pr-2">
              <input
                type="checkbox"
                checked={items.length === selectedRows.length}
                onChange={() => {
                  if (items.length === selectedRows.length) {
                    setSelectedRows([]);
                  } else {
                    setSelectedRows(questions.map((q) => q.id));
                  }
                }}
              />
              <button
                className="hover-circle relative"
                onClick={(e) => {
                  e.preventDefault();
                  actionMenuRef.current.toggleMenu();
                }}
              >
                <MoreVertical size={24} className="shrink-0 cursor-pointer" />
                <ActionMenu ref={actionMenuRef} items={actionMenuItems} />
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  const DialogCenter = () => {
    return (
      <>
        {modal.open && (
          <Dialog
            openModal={modal.open}
            closeModal={closeModal}
            modalStyle="popup"
            animation="fade-in"
            title={modal.title}
            tag={modal.label}
            tagStyle="bg-primary text-white"
            className="overflow-visible"
          >
            <p>{modal.content}</p>

            <LabeledSelectAutocomplete
              title="Choose organization"
              options={organizations.map((organization) => ({
                value: organization.id,
                name: `${organization.name}, ${organization.organization_number}`,
              }))}
              onSelect={(val) => setTargetOrganization(val)}
              defaultValue={targetOrganization}
            />

            <div className="flex w-full items-center justify-end">
              <a
                className="cursor-pointer text-blue underline"
                onClick={closeModal}
              >
                Never mind
              </a>

              <button
                className="btn ml-3 self-end"
                onClick={handleAddToOrganization}
              >
                Add to organization
              </button>
            </div>
          </Dialog>
        )}

        {isDeleting && (
          <Dialog
            openModal={isDeleting}
            closeModal={() => setIsDeleting(false)}
            modalStyle="popup"
            animation="fade-in"
            title={`You're about to delete ${selectedRows.length === 1 ? questions.find((q) => q.id === selectedRows[0]).title : `${selectedRows.length} activities`} from Emma`}
            tag="Delete global activity"
            tagStyle="bg-[#FEE5E5]"
          >
            <p>
              This action is permanent and cannot be undone. Activities already
              added to clients will not be affected. Are you sure you want to
              delete{" "}
              {selectedRows.length === 1
                ? questions.find((q) => q.id === selectedRows[0]).title
                : `${selectedRows.length} activities`}
              ?
            </p>

            <div className="flex w-full items-center justify-end">
              <a
                className="cursor-pointer text-blue underline"
                onClick={() => setIsDeleting(false)}
              >
                Cancel
              </a>

              <button
                className="btn-red ml-3 grow-0 self-end"
                onClick={handleDelete}
              >
                {selectedRows.length === 1
                  ? "Delete activity"
                  : `Delete ${selectedRows.length} activities`}
              </button>
            </div>
          </Dialog>
        )}
      </>
    );
  };

  return (
    <>
      <div className="mb-8">
        <Tabs items={["Your global", "Emma Global"]}>
          <Tab>
            <ActivityFilter tagData={tags.data} />
            <QuestionsWrapper
              items={questionsSortedByNewlyCreated().map((q, i) => (
                <Row key={`question_row_${q.id}`} question={q} />
              ))}
            />
          </Tab>
          <Tab>
            <ActivityFilter tagData={tags.data} />
            <QuestionsWrapper
              items={global_questions.map((q, i) => (
                <Row key={`question_row_${q.id}`} question={q} />
              ))}
            />
          </Tab>
        </Tabs>
      </div>
      <DialogCenter />
    </>
  );
}

const ActivityFilter = ({ tagData }) => {
  const [search, setSearch] = useAtom(searchAtom);
  const [sort, setSort] = useAtom(sortAtom);
  const [scopes, setScopes] = useAtom(scopesAtom);
  const [tags, setTags] = useAtom(tagsAtom);

  const sortOptions = [
    { value: "title.asc", label: "Name A-Ö" },
    { value: "title.desc", label: "Name Ö-A" },
    { value: "created_at.desc", label: "Newest" },
    { value: "created_at.asc", label: "Oldest" },
    { value: "updated_at.desc", label: "Updated most recent" },
    { value: "updated_at.asc", label: "Updated least recent" },
  ];

  const tagOptions = tagData.map((tag) => {
    return {
      value: String(tag.id),
      label: tag.name,
    };
  });

  const scopeRef = useRef(null);
  const tagRef = useRef(null);

  const handleSearchAndSort = React.useRef(
    debounce((search, sort, scopes, tags) => {
      Inertia.reload({
        only: ["questions", "global_questions"],
        data: {
          search: search,
          sort: sort,
          scopes: scopes?.join(","),
          tags: tags?.join(","),
        },
      });
    }, 500),
  ).current;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (!params.get("scopes") || scopes === null) {
      const initialSearch = params.get("search") || "";
      const initialSort = params.get("sort") || "";
      const initialScopes = params.get("scopes")
        ? params.get("scopes").split(",")
        : ["other", ...SCOPE_OPTIONS.map((s) => s.value)];
      const initialTags = params.get("tags")
        ? params.get("tags").split(",")
        : ["other", ...tagOptions.map((s) => s.value)];

      setSearch(initialSearch);
      setSort(initialSort);
      setScopes(initialScopes);
      setTags(initialTags);

      scopeRef.current.setStateOptions((prev) => {
        return prev.map((scope) => {
          return {
            ...scope,
            checked: initialScopes.includes(scope.value),
          };
        });
      });

      tagRef.current.setStateOptions((prev) => {
        return prev.map((tag) => {
          return {
            ...tag,
            checked: initialTags.includes(tag.value),
          };
        });
      });

      refreshData(initialSearch, initialSort, initialScopes, initialTags);
    }
  }, []);

  const refreshData = (search, sort, scopes, tags) => {
    handleSearchAndSort.cancel();
    handleSearchAndSort(search, sort, scopes, tags);
  };

  return (
    <div className="mb-4 flex flex-col items-end justify-between gap-4 sm:flex-row sm:items-center">
      <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:items-end">
        <LabeledMultiSelectAutocomplete
          title="Categories and modules"
          className="w-full sm:w-auto"
          ref={tagRef}
          placeholder="All"
          options={[{ value: "other", label: "Other" }, ...tagOptions]}
          defaultValue={tags || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setTags(values);
            refreshData(search, sort, scopes, values);
          }}
        />

        <LabeledMultiSelectAutocomplete
          className="w-full sm:w-auto"
          ref={scopeRef}
          placeholder="Scopes"
          options={[{ value: "other", label: "Other" }, ...SCOPE_OPTIONS]}
          defaultValue={scopes || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setScopes(values);
            refreshData(search, sort, values, tags);
          }}
        />

        <LabeledInput
          value={search}
          type="search"
          placeholder="Search"
          className="w-full sm:w-auto"
          unit={search === "" ? <Search className="relative -top-1" /> : null}
          onChange={(e) => {
            const value = e.target.value;
            setSearch(e.target.value);
            refreshData(value, sort, scopes, tags);
          }}
        />
      </div>

      <Dropdown
        options={sortOptions}
        title="Sort on"
        placeholder="Name A-Ö"
        onSelect={(value) => {
          setSort(value);
          refreshData(search, value, scopes, tags);
        }}
      />
    </div>
  );
};

const HeaderActions = () => {
  const setNewActivityDialogOpen = useSetAtom(isNewActivityOpenAtom);

  return (
    <button
      className="btn-blue shrink-0 grow-0"
      onClick={() => setNewActivityDialogOpen(true)}
    >
      New activity
    </button>
  );
};

Index.layout = (page) => {
  return (
    <AppLayout title="Global activities">
      <OrganizationLayout
        header={"Global activities"}
        byline={`Create activity templates that can be attached to any organization`}
        action={<HeaderActions />}
        icon={
          <LayoutIcon>
            <TeamsIcon size={40} className="text-black" />
          </LayoutIcon>
        }
      >
        {page}
      </OrganizationLayout>
      <LoadingProvider>
        <NewActivityChooseType source="agency" />
      </LoadingProvider>
    </AppLayout>
  );
};
